import { Brand, Vehicle } from '../types/catalog';

export interface AssistantLink {
  label: string;
  to: string;
  external?: boolean;
}

export interface AssistantReply {
  text: string;
  links?: AssistantLink[];
  vehicleIds?: string[];
  whatsapp?: string;
  chips?: string[];
}

export const USED_CATALOG_URL = 'https://catalogo.automotoresvcars.com/';

export const startChips = ['Ver 0 km', 'Simular cuota', 'Chequeo crediticio', 'Entregar mi usado', 'Hablar con un asesor'];

const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, ' ');

const has = (q: string, words: string[]) => words.some((w) => q.includes(w));

const bodyWords: { words: string[]; body: string; label: string }[] = [
  { words: ['suv', 'camioneta'], body: 'SUV', label: 'SUV' },
  { words: ['sedan'], body: 'Sedán', label: 'sedán' },
  { words: ['hatch', 'hatchback'], body: 'Hatchback', label: 'hatchback' },
  { words: ['pick up', 'pickup', 'pick-up', 'chata'], body: 'Pick-up', label: 'pick-up' },
  { words: ['utilitario', 'furgon'], body: 'Utilitario', label: 'utilitarios' },
];

/** Respuestas por palabras clave. No usa IA: sólo responde con información de la página. */
export function answer(input: string, vehicles: Vehicle[], brands: Brand[]): AssistantReply {
  const q = ` ${norm(input)} `;
  const published = vehicles.filter((v) => v.published);

  // Modelos puntuales
  const models = published.filter((v) => {
    const m = norm(v.model).trim();
    return m.length > 1 && q.includes(` ${m} `);
  });
  // Marcas
  const brand = brands.find((b) => q.includes(` ${norm(b.name).trim()} `) || (b.id === 'volkswagen' && has(q, [' vw '])));

  if (has(q, ['hola', 'buenas', 'buen dia', 'buenos dias', 'buenas tardes', 'buenas noches'])) {
    return {
      text: '¡Hola! Soy el asistente virtual de VCARS. Te ayudo a encontrar tu 0 km, simular una cuota o hablar con un asesor. ¿Qué estás buscando?',
      chips: startChips,
    };
  }

  if (models.length) {
    const fin = has(q, ['cuota', 'financ', 'anticipo', 'simul', 'precio', 'cuanto', 'sale', 'vale']);
    return {
      text: fin
        ? `Esta es la referencia de ${models[0].model}. En el simulador podés ajustar el anticipo y el plazo.`
        : `Encontré ${models.length === 1 ? 'esta unidad' : 'estas unidades'}:`,
      vehicleIds: models.slice(0, 3).map((v) => v.id),
      links: [{ label: 'Simular este auto', to: `/simulador?v=${models[0].id}` }],
      whatsapp: `Hola VCARS, quiero consultar por el ${models[0].model} ${models[0].version}.`,
    };
  }

  if (brand) {
    const list = published.filter((v) => v.brandId === brand.id);
    return {
      text: list.length
        ? `Tenemos ${list.length} ${list.length === 1 ? 'versión' : 'versiones'} de ${brand.name}. Te muestro algunas:`
        : `Por ahora no hay unidades de ${brand.name} publicadas, pero un asesor puede consultarlo por vos.`,
      vehicleIds: list.slice(0, 3).map((v) => v.id),
      links: [{ label: `Ver todo ${brand.name}`, to: `/marcas/${brand.slug}` }],
      whatsapp: list.length ? undefined : `Hola VCARS, busco un 0 km ${brand.name}.`,
    };
  }

  const body = bodyWords.find((b) => has(q, b.words));
  if (body && !has(q, ['usado'])) {
    const list = published.filter((v) => v.bodyType === body.body);
    return {
      text: list.length ? `Estos son algunos ${body.label} 0 km:` : `No hay ${body.label} publicados en este momento.`,
      vehicleIds: list.slice(0, 3).map((v) => v.id),
      links: [{ label: `Ver todos los ${body.label}`, to: `/catalogo?body=${encodeURIComponent(body.body)}` }],
    };
  }

  if (has(q, ['usado', 'permuta', 'entregar mi', 'entrego', 'tomar mi auto', 'toman'])) {
    return {
      text: 'Sí, tomamos tu usado como parte de pago. Dejá tu consulta con los datos de tu auto y un asesor te pasa una cotización. También podés ver nuestro stock de usados seleccionados.',
      links: [
        { label: 'Ver usados VCARS', to: USED_CATALOG_URL, external: true },
        { label: 'Ver 0 km', to: '/catalogo' },
      ],
      whatsapp: 'Hola VCARS, quiero entregar mi usado como parte de pago de un 0 km.',
    };
  }

  if (has(q, ['chequeo', 'veraz', 'score', 'califico', 'aprueb', 'aprob', 'credito', 'prestamo'])) {
    return {
      text: 'Con el chequeo crediticio un asesor revisa tu caso y te dice qué alternativas tenés. No es una aprobación: la decisión final es de la entidad financiera.',
      links: [{ label: 'Hacer chequeo crediticio', to: '/chequeo' }],
    };
  }

  if (has(q, ['entrega inmediata', 'inmediata', 'stock', 'disponible'])) {
    const list = published.filter((v) => v.availability === 'Entrega inmediata' || v.availability === 'Disponible');
    return {
      text: list.length ? 'Estas unidades figuran disponibles. Confirmá la entrega con un asesor:' : 'Consultá la disponibilidad con un asesor.',
      vehicleIds: list.slice(0, 3).map((v) => v.id),
      links: [{ label: 'Ver catálogo', to: '/catalogo' }],
    };
  }

  if (has(q, ['financ', 'cuota', 'simul', 'tasa', 'tna', 'anticipo', 'plazo', 'banco', '100%', 'uva'])) {
    return {
      text: 'Podés simular tu cuota con las circulares oficiales de cada marca (Fiat, Jeep, Ram y Citroën) o con las condiciones generales. Elegís auto, monto y plazo, y ves la cuota estimada. Todo está sujeto a aprobación crediticia.',
      links: [
        { label: 'Ir al simulador', to: '/simulador' },
        { label: 'Hacer chequeo crediticio', to: '/chequeo' },
      ],
      chips: ['Fiat', 'Jeep', 'Ver 0 km'],
    };
  }

  if (has(q, ['compar'])) {
    return { text: 'Elegí hasta 3 autos y comparalos lado a lado: precio, cuota, equipamiento y más.', links: [{ label: 'Ir al comparador', to: '/comparar' }] };
  }

  if (has(q, ['flota', 'empresa', 'pyme', 'cuit', 'varias unidades'])) {
    return {
      text: 'Para flotas y ventas especiales armamos una propuesta a medida. Contanos cuántas unidades y de qué tipo.',
      whatsapp: 'Hola VCARS, quiero cotizar una flota para mi empresa.',
    };
  }

  if (has(q, ['reclamo', 'administracion', 'factura'])) {
    return { text: 'Para administración general o reclamos escribinos al 11 2468-7787.', whatsapp: 'Hola VCARS, me comunico por administración.' };
  }

  if (has(q, ['horario', 'direccion', 'donde estan', 'ubicacion', 'local', 'telefono', 'contacto', 'whatsapp', 'asesor', 'vendedor', 'humano', 'persona'])) {
    return {
      text: 'Somos una agencia online. Área ventas: 11 5644-9518 · Administración y reclamos: 11 2468-7787. Zona Oeste: próximamente.',
      whatsapp: 'Hola VCARS, quiero hablar con un asesor.',
    };
  }

  if (has(q, ['0 km', '0km', 'catalogo', 'autos', 'auto', 'modelos', 'nuevo', 'marcas', 'ver'])) {
    return {
      text: 'Tenemos 0 km de varias marcas. Podés filtrar por marca, tipo, precio y cuota.',
      links: [
        { label: 'Ver catálogo 0 km', to: '/catalogo' },
        { label: 'Ver marcas', to: '/marcas' },
      ],
      chips: ['SUV', 'Sedán', 'Pick-up', 'Hatchback'],
    };
  }

  if (has(q, ['gracias', 'genial', 'perfecto', 'listo'])) {
    return { text: '¡De nada! Si necesitás algo más, acá estoy.', chips: startChips };
  }

  return {
    text: 'No estoy seguro de haberte entendido. Probá con una marca o modelo (por ejemplo "Cronos" o "Jeep"), o elegí una opción. También podés hablar con un asesor.',
    chips: startChips,
    whatsapp: `Hola VCARS, tengo una consulta: ${input.slice(0, 200)}`,
  };
}
